import React, { forwardRef } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import DynamicSegmentPreviewTable, {
  type DynamicSegmentPreviewTableHandle
} from './DynamicSegmentPreviewTable'
import { EngagementNudgeBody } from './EngagementNudgeBody'
import { useDynamicSegmentPreview } from 'src/lib/hooks/useDynamicSegmentPreview'

interface SegmentPreviewProps {
  isDetailPanelVisible?: boolean
  /**
   * True: details column is in the flex layout beside this preview (manager view). The engagement overlay spans the full preview column.
   * False (default): details are an overlaid sidesheet on a full-width preview (creation/edit); overlay clears 440px on the right.
   */
  detailsBesidePreview?: boolean
  /** When false: engagement nudge only offers Preview Data (no count / audience size). */
  engagementShowCount?: boolean
  /** When true: same table + blur nudge layout as after edits, but no auto preview API until user clicks Preview Data */
  nudgeOnly?: boolean
  onEngageFullPreview?: () => void
  /** Hosted views (e.g. SSO-analytics overlay) sit on their own surface — drop the table's own background. */
  backgroundTransparent?: boolean
  /** ShopPass: skip the placeholder table and show the page behind (Sankey) through zinc-50 + backdrop blur. */
  blurBehindContent?: boolean
  /** Skip nested mount fades — host already animates this column (e.g. ShopPass staged enter). */
  skipEnterAnimation?: boolean
  /** Drop the preview column's top padding (e.g. ShopPass staged enter sits flush under the header). */
  flushTop?: boolean
}

const SegmentPreview = forwardRef<DynamicSegmentPreviewTableHandle, SegmentPreviewProps>(
  (
    {
      isDetailPanelVisible = false,
      detailsBesidePreview = false,
      engagementShowCount = true,
      nudgeOnly = false,
      onEngageFullPreview,
      backgroundTransparent = false,
      blurBehindContent = false,
      skipEnterAnimation = false,
      flushTop = false
    },
    ref
  ) => {
    const {
      audienceCount,
      tickerIndex,
      paginationState,
      setPaginationState,
      dimensionIds,
      dimensionNames,
      hasConditions,
      showEngagementOverlay,
      tablePreviewData,
      knownTotalRowCount,
      previewTableLoading,
      countJob,
      previewJob,
      fetchAudienceCount,
      handlePreviewDataClick
    } = useDynamicSegmentPreview({ nudgeOnly, onEngageFullPreview })

    const engagementRight = detailsBesidePreview ? 0 : isDetailPanelVisible ? 440 : 0

    const previewColumnEntryDurationSec = 1.5
    const engagementOverlayOpacityDurationSec = 0.3
    const tablePreviewOpacityDurationSec = 1
    const previewTableRevealDelaySec = 1

    const showPlaceholderTable = !(blurBehindContent && showEngagementOverlay)

    return (
      <div className='flex h-full min-h-0 w-full min-w-0 flex-col'>
        <motion.div
          className={`relative flex h-full min-h-0 w-full min-w-0 flex-col ${
            flushTop ? '' : 'pt-4'
          }`}
          initial={skipEnterAnimation ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={
            skipEnterAnimation
              ? { duration: 0 }
              : {
                  duration: previewColumnEntryDurationSec,
                  ease: 'easeOut',
                  delay: 0.5
                }
          }
        >
          {showPlaceholderTable ? (
            <motion.div
              className={`relative flex min-h-0 flex-1 flex-col transition-[filter] duration-300 ease-out ${
                showEngagementOverlay ? 'blur-sm' : ''
              }`}
              initial={skipEnterAnimation ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={
                skipEnterAnimation
                  ? { duration: 0 }
                  : {
                      duration: tablePreviewOpacityDurationSec,
                      ease: 'easeOut',
                      delay: previewTableRevealDelaySec
                    }
              }
            >
              <DynamicSegmentPreviewTable
                ref={ref}
                backgroundTransparent={backgroundTransparent || blurBehindContent}
                previewData={tablePreviewData}
                loading={previewTableLoading}
                pagination={{
                  paginationState,
                  setPaginationState
                }}
                dimensionIds={dimensionIds}
                dimensionNames={dimensionNames}
                knownTotalRowCount={knownTotalRowCount}
                isDetailPanelVisible={!detailsBesidePreview && isDetailPanelVisible}
              />
            </motion.div>
          ) : (
            <div className='min-h-0 flex-1' />
          )}

          <AnimatePresence>
            {showEngagementOverlay && (
              <motion.div
                key='preview-engagement-overlay'
                className='pointer-events-none absolute inset-y-0 left-0 z-20'
                initial={{ opacity: 0, right: engagementRight }}
                animate={{ opacity: 1, right: engagementRight }}
                exit={{ opacity: 0 }}
                transition={{
                  opacity: {
                    duration: engagementOverlayOpacityDurationSec,
                    ease: 'easeOut',
                    delay: 0
                  },
                  right: { type: 'spring', duration: 0.5, bounce: 0.1 }
                }}
              >
                <div
                  aria-hidden
                  className={
                    blurBehindContent
                      ? 'pointer-events-auto absolute inset-0'
                      : 'pointer-events-auto absolute inset-0 bg-gradient-to-t from-zinc-50 to-zinc-50/50'
                  }
                />
                <div className='pointer-events-none absolute inset-0 flex min-w-0 w-full flex-col justify-center pb-20'>
                  <div className='pointer-events-auto min-w-0 w-full max-w-full'>
                    <EngagementNudgeBody
                      hasConditions={hasConditions}
                      showCount={engagementShowCount}
                      countJob={countJob}
                      previewJob={previewJob}
                      tickerIndex={tickerIndex}
                      audienceCount={audienceCount}
                      fetchAudienceCount={fetchAudienceCount}
                      onPreviewDataClick={handlePreviewDataClick}
                      dimensionIds={dimensionIds}
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    )
  }
)

SegmentPreview.displayName = 'SegmentPreview'

export default SegmentPreview
